import { Flex, Indicator, Tabs, Text } from "@mantine/core";
import { useTimeout } from "@mantine/hooks";
import { useState } from "react";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../../stores";
import { EligibilityStatus, SaleProgress } from "../../../../types";
import { CustomerView } from "./CustomerView";
import { OffersView } from "./OffersView";

function isOlderThanFiveMinutes(timestamp: number) {
    return Date.now() - timestamp > 5 * 60 * 1000;
}

import type { CustomerOffersView } from "./CustomerOffers";

interface CustomerOffersLoadedProps {
    startedTimestamp: number;
    isChat: boolean;
    view?: CustomerOffersView;
}

export function CustomerOffersLoaded({ startedTimestamp, isChat, view = "tabs" }: CustomerOffersLoadedProps) {
    const { sessionId, isSimulatedVoiceTask, features } = useSalesContext();
    const [activeTab, setActiveTab] = useState<string | null>(isSimulatedVoiceTask ? "offers" : "customer");

    const switchToOffersTabTimeout = useTimeout(
        () => setActiveTab("offers"),
        isOlderThanFiveMinutes(startedTimestamp) ? 0 : 90_000,
        { autoInvoke: true },
    );

    const handleSelectTab = (value: string | null) => {
        switchToOffersTabTimeout.clear();
        setActiveTab(value);
    };

    const showOffersIndicator = useProductOfferStore
        .getState()
        .getProducts(sessionId)
        .some((p) => p.status === EligibilityStatus.Eligible && p.saleProgress === SaleProgress.InProgress);

    if (view === "offers") {
        return <OffersView isChat={isChat} />;
    }

    return (
        <Tabs value={activeTab} data-testid="customer-offers-tabs" onChange={handleSelectTab}>
            <Tabs.List grow>
                <Tabs.Tab value="customer" fw={activeTab === "customer" ? 700 : 400}>
                    Customer
                </Tabs.Tab>

                <Tabs.Tab value="offers">
                    <Flex align="center" justify="center">
                        <Text size="sm" fw={activeTab === "offers" ? 700 : 400}>
                            Offers
                        </Text>
                        {showOffersIndicator && <Indicator size={6} processing color="primary.5" ml={4} mb="md" />}
                    </Flex>
                </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="customer" data-testid="customer-tab-panel">
                <CustomerView hideCustomerInsights={isChat && !features.isChatDeviceDetectionEnabled} />
            </Tabs.Panel>

            <Tabs.Panel value="offers" data-testid="offers-tab-panel">
                <OffersView isChat={isChat} />
            </Tabs.Panel>
        </Tabs>
    );
}
